import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  Divider,
  Link
} from '@mui/material';
import axios from 'axios';
import BankAccountNumberMatch from './BankAccountNumberMatch';
import ProcessProgressModal from './ProcessProgressModal';
import { streamProcessRequest } from '../hooks/useProcessStream';
import { ProcessProgressEvent } from '../types/processProgress';

const API_URL = process.env.REACT_APP_API_URL || '/pdfExtractorHttp';
const TARGET_ENVIRONMENT = process.env.REACT_APP_TARGET_ENVIRONMENT || 'prd';

let MATCH_ANALYSIS_URL = '';
switch (TARGET_ENVIRONMENT) {
  case 'prd':
    MATCH_ANALYSIS_URL = 'https://asia-southeast2-cimb-smart-production.cloudfunctions.net/jojo-ocr-function/match-analysis';
    break;
  case 'uat':
    MATCH_ANALYSIS_URL = 'https://asia-southeast2-cimb-smart-uat.cloudfunctions.net/jojo-ocr-function/match-analysis';
    break;
  default:
    MATCH_ANALYSIS_URL = 'https://asia-southeast2-cimb-smart-production.cloudfunctions.net/jojo-ocr-function/match-analysis';
}

interface AnalysisResult {
  dateMatch: {
    isMatched: boolean;
    matchedDate: string;
    extractedDates: string[];
    status: 'success' | 'error';
    originalDate: string;
  };
  invoiceMatch: {
    isMatched: boolean;
    matchedInvoice: string | null;
    status: 'success' | 'error';
    originalInvoice: string;
  };
  amountMatch: {
    isMatched: boolean;
    matchedAmount: string;
    extractedNumbers: string[];
    status: 'success' | 'error';
    originalAmount: string;
  };
  bankAccountNumberMatch: {
    isMatched: boolean;
    matchedBankAccountNumber: string;
    status: 'success' | 'error';
    originalBankAccountNumber: string;
  };
}

interface OCRSimilarityResult {
  matchValueAmount: number;
  matchValueInvoice: number;
  matchValueVendor: number;
  matchValueSemantic: number;
  matchValueCategory: number;
  similarityPercentage: number;
  confidenceLevel: 'High' | 'Medium' | 'Low';
  needsReview: boolean;
  extractedVendorName?: string;
  referenceVendorName?: string;
  weights?: {
    amount: number;
    invoiceNumber: number;
    vendor: number;
    semanticText: number;
    category: number;
    invoiceDate: number;
  };
}

interface Result {
  refId: string;
  description: string;
  similarityCheck: any[];
  status_ocr: string;
  amount: string;
  categoryName: string;
  categoryType: string;
  invoiceNumber: string;
  parentUniqueId: string;
  tableName: string;
  timestamp: Date;
  transactionDate: string;
  bankAccountNumber: string;
  analysis: AnalysisResult;
  dataSource: string;
  suspected?: boolean;
  suspectedMatches?: string[];
  // OCR Similarity fields
  ocrSimilarity?: OCRSimilarityResult;
  referenceRefId?: string;
  // Download URL
  downloadUrl?: string;
}

interface MatchAnalysisResult {
  dates: string[];
  numbers: string[];
  invoiceMatch: boolean;
  matchedInvoice: string | null;
  datesMatch: boolean;
  matchedDates: string;
  amountMatch: boolean;
  matchedAmount: string;
  bankAccountNumberMatch: boolean;
  matchedBankAccountNumber: string;
}

const RefIdForm: React.FC = () => {
  const [refId, setRefId] = useState('');
  const [token, setToken] = useState('');
  const [referenceRefId, setReferenceRefId] = useState('');
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [showReferenceInput, setShowReferenceInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [patchLoading, setPatchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [results, setResults] = useState<Result[]>([]);
  const [analysisResults, setAnalysisResults] = useState<Record<string, MatchAnalysisResult>>({});
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressEvents, setProgressEvents] = useState<ProcessProgressEvent[]>([]);
  const [progressError, setProgressError] = useState<string | null>(null);

  // Debug: Log when analysis results change
  useEffect(() => {
    console.log('Analysis results changed:', analysisResults);
  }, [analysisResults]);

  const getMatchAnalysis = async (data: Result): Promise<MatchAnalysisResult> => {
    try {
      const requestData = {
        description: data.description,
        bankAccountNumber: data.bankAccountNumber,
        invoiceNumber: data.invoiceNumber,
        amount: data.amount,
        transactionDate: data.transactionDate
      };
      console.log('Match analysis request:', requestData);
      
      const response = await axios.post(MATCH_ANALYSIS_URL, requestData);
      console.log('Match analysis response:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('Error getting match analysis:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    setResults([]);
    setAnalysisResults({});
    setProgressEvents([]);
    setProgressError(null);
    setProgressOpen(true);
    console.log('analysis_url:', MATCH_ANALYSIS_URL);
    console.log('target environment', TARGET_ENVIRONMENT);
    console.log('build date', process.env.REACT_APP_BUILD_DATE)
    try {
      const response = await streamProcessRequest({
        url: `${API_URL}/process`,
        body: {
          refId,
          token: showTokenInput ? token : undefined,
          referenceRefId: showReferenceInput ? referenceRefId : undefined,
        },
        onProgress: (event) => {
          setProgressEvents((current) => [...current, event]);
        },
      });
      
      if (response.message === 'Error' && response.error) {
        setProgressError(response.error);
        setError(response.error);
        if (response.error.includes('Token is required')) {
          setShowTokenInput(true);
        }
      } else if (response.message === 'No records found in database or GCS') {
        const notFoundMessage = `Ref ID "${refId}" not found in the system. Please check if the Ref ID exists and try again.`;
        setProgressError(notFoundMessage);
        setError(notFoundMessage);
      } else if (response.message && response.message !== 'Success') {
        setProgressError(response.message);
        setError(response.message);
      } else if (response.message === 'Success') {
        setProgressOpen(false);
        setSuccess(response.message);
        const processResults = Array.isArray(response.data) ? response.data : [response.data];
        
        // Apply deduplication to results for display (same logic as analysis)
        const deduplicatedResults = processResults.reduce((acc: Record<string, Result>, result: Result, index: number) => {
          const key = `${result.refId}_${result.categoryType}_${result.tableName}`;
          
          if (!acc[key]) {
            acc[key] = result;
          } else {
            const existingAmount = acc[key].amount || '';
            const currentAmount = result.amount || '';
            const existingTimestamp = acc[key].timestamp || '';
            const currentTimestamp = result.timestamp || '';
            
            if (existingAmount === '' && currentAmount !== '') {
              acc[key] = result;
            } else if (existingAmount !== '' && currentAmount === '') {
              // Keep existing non-empty amount
            } else if (existingAmount === currentAmount) {
              if (currentTimestamp > existingTimestamp) {
                acc[key] = result;
              }
            }
          }
          return acc;
        }, {} as Record<string, Result>);
        
        const finalResults = Object.values(deduplicatedResults) as Result[];
        console.log('Original results count:', processResults.length);
        console.log('Deduplicated results count:', finalResults.length);
        setResults(finalResults);

        // Clear analysis results and set loading state
        console.log('Clearing analysis results before new analysis');
        setAnalysisResults({});
        setAnalysisLoading(true);
        
        // Force a re-render to ensure state is cleared
        setTimeout(() => {
          console.log('Analysis results after timeout:', analysisResults);
        }, 100);
        
        // Get analysis results for each unique record (deduplicate by key including tableName, prioritize latest timestamp)
        const uniqueResults = processResults.reduce((acc: Record<string, Result>, result: Result, index: number) => {
          const key = `${result.refId}_${result.categoryType}_${result.tableName}`;
          console.log(`Processing result ${index + 1}: refId=${result.refId}, categoryType=${result.categoryType}, amount="${result.amount}", tableName=${result.tableName}, timestamp=${result.timestamp}`);
          console.log(`Generated key: ${key}`);
          
          if (!acc[key]) {
            // First result for this key
            console.log(`First result for key: ${key}, storing result with amount: "${result.amount}"`);
            acc[key] = result;
          } else {
            // If we already have a result for this key, prioritize based on amount and timestamp
            const existingAmount = acc[key].amount || '';
            const currentAmount = result.amount || '';
            const existingTimestamp = acc[key].timestamp || '';
            const currentTimestamp = result.timestamp || '';
            
            console.log(`Duplicate key found: ${key}, existing amount: "${existingAmount}", current amount: "${currentAmount}"`);
            console.log(`Existing timestamp: ${existingTimestamp}, current timestamp: ${currentTimestamp}`);
            
            if (existingAmount === '' && currentAmount !== '') {
              // Replace empty amount with non-empty amount
              console.log(`Replacing empty amount with non-empty amount for key: ${key}`);
              acc[key] = result;
            } else if (existingAmount !== '' && currentAmount === '') {
              // Keep existing non-empty amount, ignore empty amount
              console.log(`Keeping non-empty amount, ignoring empty amount for key: ${key}`);
            } else if (existingAmount === currentAmount) {
              // Both have same amount, keep the one with latest timestamp
              if (currentTimestamp > existingTimestamp) {
                console.log(`Same amount, keeping newer timestamp for key: ${key}`);
                acc[key] = result;
              } else {
                console.log(`Same amount, keeping existing timestamp for key: ${key}`);
              }
            } else {
              // Both have different amounts, keep the existing one
              console.log(`Different amounts, keeping existing for key: ${key}`);
            }
          }
          return acc;
        }, {} as Record<string, Result>);
        
        console.log('Original processResults count:', processResults.length);
        console.log('ProcessResults data:', processResults.map((r: Result) => ({ refId: r.refId, categoryType: r.categoryType, amount: r.amount })));
        console.log('Unique results keys:', Object.keys(uniqueResults));
        console.log('Unique results count:', Object.keys(uniqueResults).length);
        console.log('Unique results data:', (Object.values(uniqueResults) as Result[]).map((r: Result) => ({ refId: r.refId, categoryType: r.categoryType, amount: r.amount })));
        
        const analysisPromises = (Object.values(uniqueResults) as Result[]).map(async (result: Result) => {
          try {
            console.log(`Processing analysis for refId: ${result.refId}, categoryType: ${result.categoryType}, tableName: ${result.tableName}`);
            const analysis = await getMatchAnalysis(result);
            const key = `${result.refId}_${result.categoryType}_${result.tableName}`;
            console.log(`Generated key: ${key}`);
            return { refId: result.refId, categoryType: result.categoryType, tableName: result.tableName, analysis, key };
          } catch (error) {
            console.error(`Error getting analysis for ${result.refId}:`, error);
            return { refId: result.refId, categoryType: result.categoryType, tableName: result.tableName, analysis: null, key: `${result.refId}_${result.categoryType}_${result.tableName}` };
          }
        });

        const analysisResultsArray = await Promise.all(analysisPromises);
        const analysisMap = analysisResultsArray.reduce((acc, { refId, categoryType, tableName, analysis, key }) => {
          if (analysis) {
            acc[key] = analysis;
            console.log(`Storing analysis for key: ${key}`);
          }
          return acc;
        }, {} as Record<string, MatchAnalysisResult>);
        
        console.log('Setting new analysis results:', analysisMap);
        console.log('Analysis results keys:', Object.keys(analysisMap));
        setAnalysisResults(analysisMap);
        setAnalysisLoading(false);
      } else {
        setError('Unexpected response format');
      }
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to process request. Please try again.';

      setProgressError(errorMessage);
      setError(errorMessage);

      if (errorMessage.includes('Token is required')) {
        setShowTokenInput(true);
      }
      
      // Clear analysis loading state on error
      setAnalysisLoading(false);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildDate = process.env.REACT_APP_BUILD_DATE || 'unknown';

  const handlePatchOcrStatus = async () => {
    const trimmed = refId.trim();
    if (!trimmed) {
      setError('Enter LINE Ref ID(s) first (comma-separated allowed)');
      return;
    }
    setPatchLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: Record<string, string> = { ids: trimmed };
      if (token.trim()) payload.token = token.trim();
      const response = await axios.post(`${API_URL}/patch-ocr-status`, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 180000,
      });
      const msg =
        response.data?.data?.message ||
        response.data?.message ||
        'OCR status patched (line clear + header rollup)';
      setSuccess(
        `${msg} — ids: ${(response.data?.ids || []).join(', ') || trimmed}. Coba Approve lagi.`,
      );
    } catch (err: any) {
      const data = err?.response?.data;
      setError(
        data?.error ||
          data?.message ||
          data?.data?.message ||
          (err instanceof Error ? err.message : 'Failed to patch OCR status'),
      );
    } finally {
      setPatchLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 1200,
          width: '100%'
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          OCR Similarity Detection
        </Typography>
        <Typography variant="body1" gutterBottom align="center" color="text.secondary">
          Enter the Ref ID to start the OCR similarity detection process
        </Typography>
        <Typography variant="body2" align="center" sx={{ mb: 1 }}>
          <Link href="/docs/similarity-query-period.html" underline="hover">
            Docs: Similarity query period (1-year lookback)
          </Link>
        </Typography>
        
        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Ref IDs must exist in the system to be processed
          </Typography>
        </Alert>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Ref ID"
            variant="outlined"
            value={refId}
            onChange={(e) => setRefId(e.target.value)}
            margin="normal"
            required
            disabled={loading || patchLoading}
            helperText="LINE settlement Ref ID (PCS). For Clear+Rollup, comma-separated IDs allowed."
          />
          
          <Collapse in={showTokenInput}>
            <TextField
              fullWidth
              label="Token"
              variant="outlined"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              margin="normal"
              required={showTokenInput}
              disabled={loading}
              helperText="Token is required for updating similarity"
            />
          </Collapse>
          
          <Collapse in={showReferenceInput}>
            <TextField
              fullWidth
              label="Reference Ref ID (Optional)"
              variant="outlined"
              value={referenceRefId}
              onChange={(e) => setReferenceRefId(e.target.value)}
              margin="normal"
              disabled={loading}
              helperText="Enter a reference Ref ID to compare OCR results for similarity analysis"
            />
          </Collapse>
          
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            onClick={() => setShowReferenceInput(!showReferenceInput)}
            disabled={loading}
            sx={{ mt: 1 }}
          >
            {showReferenceInput ? 'Hide Reference ID Input' : 'Add Reference ID for Similarity Check'}
          </Button>
          
          <Button
            fullWidth
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading || patchLoading}
            sx={{ mt: 2 }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Processing...
              </Box>
            ) : 'Start Process'}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            color="warning"
            onClick={handlePatchOcrStatus}
            disabled={loading || patchLoading || !refId.trim()}
            sx={{ mt: 1.5 }}
          >
            {patchLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Clearing OCR + rollup...
              </Box>
            ) : 'Clear OCR On Progress + Rollup Header'}
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            PCS only: pakai Ref ID <strong>line</strong> settlement (bukan Ref PPX/UI). Bisa banyak: 208203,209429
          </Typography>
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {error}
            </Typography>
            {error.includes('not found in the system') && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Suggestions:</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  • Verify the Ref ID is correct and exists in the system
                </Typography>
                <Typography variant="body2" component="div">
                  • Try using a different Ref ID that exists in the system
                </Typography>
                <Typography variant="body2" component="div">
                  • Check if the document has been processed and stored
                </Typography>
                {showReferenceInput && referenceRefId && (
                  <Typography variant="body2" component="div" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Note: Both Ref ID and Reference Ref ID must exist for similarity comparison
                  </Typography>
                )}
              </Box>
            )}
          </Alert>
        )}

        {success && !error && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        {results.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              OCR Results ({results.length} total)
            </Typography>
            {results.map((result, index) => (
              <Box key={index}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  color: 'text.secondary'
                }}>
                  <Typography variant="subtitle1">
                    Result {index + 1} of {results.length}
                  </Typography>
                  <Divider sx={{ flex: 1, mx: 2 }} />
                </Box>
                <Box sx={{ mb: 4 }}>
                  {result.description && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" color="primary">
                          Ref ID: {refId}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Category Type:
                          </Typography>
                          <Chip 
                            label={result.categoryType}
                            color="primary"
                            sx={{ 
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              px: 2,
                              py: 1
                            }}
                          />
                          {result.downloadUrl && (
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              component="a"
                              href={result.downloadUrl}
                              download
                              target="_blank"
                              sx={{ ml: 1 }}
                            >
                              ⬇️ Download Image
                            </Button>
                          )}
                        </Box>
                      </Box>
                      {result.suspected && (
                        <Alert 
                          severity="warning" 
                          sx={{ 
                            mb: 2,
                            '& .MuiAlert-icon': {
                              fontSize: '2rem'
                            }
                          }}
                        >
                          <Typography variant="h6" gutterBottom>
                            Suspected Content Detected
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            The following blacklisted items were found in the description:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {result.suspectedMatches?.map((match, index) => (
                              <Chip
                                key={index}
                                label={match}
                                color="warning"
                                variant="outlined"
                                sx={{ 
                                  fontSize: '0.9rem',
                                  fontWeight: 'bold'
                                }}
                              />
                            ))}
                          </Box>
                        </Alert>
                      )}
                      
                      {/* OCR Similarity Display */}
                      {result.ocrSimilarity && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" gutterBottom color="primary">
                            Reference Field Match
                          </Typography>
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                              Reference Field Match compares amount, invoice number, vendor name, category, and
                              extracted OCR fields against the selected Reference Ref ID. This score uses structured
                              field matching plus semantic OCR text similarity.
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Bobot (weights):</strong>{' '}
                              Amount {result.ocrSimilarity.weights?.amount ?? 25}% ·
                              Invoice {result.ocrSimilarity.weights?.invoiceNumber ?? 20}% ·
                              Vendor {result.ocrSimilarity.weights?.vendor ?? 20}% ·
                              Semantic {result.ocrSimilarity.weights?.semanticText ?? 25}% ·
                              Category {result.ocrSimilarity.weights?.category ?? 10}% ·
                              Invoice date {result.ocrSimilarity.weights?.invoiceDate ?? 0}% (display only, not scored)
                            </Typography>
                          </Alert>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                            <Paper sx={{ p: 2, minWidth: 200, bgcolor: 'background.paper' }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Reference Field Match Score
                              </Typography>
                              <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                                {result.ocrSimilarity.similarityPercentage}%
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: result.ocrSimilarity.confidenceLevel === 'High' ? 'success.main' : 
                                          result.ocrSimilarity.confidenceLevel === 'Medium' ? 'warning.main' : 'error.main',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {result.ocrSimilarity.confidenceLevel === 'High' ? '✅' : 
                                   result.ocrSimilarity.confidenceLevel === 'Medium' ? '⚠️' : '❌'} 
                                  {result.ocrSimilarity.confidenceLevel} Confidence
                                </Typography>
                              </Box>
                            </Paper>
                            
                            <Paper sx={{ p: 2, minWidth: 200, bgcolor: 'background.paper' }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Amount Match (bobot {result.ocrSimilarity.weights?.amount ?? 25}%)
                              </Typography>
                              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                                {result.ocrSimilarity.matchValueAmount}%
                              </Typography>
                            </Paper>
                            
                            <Paper sx={{ p: 2, minWidth: 200, bgcolor: 'background.paper' }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Invoice Match (bobot {result.ocrSimilarity.weights?.invoiceNumber ?? 20}%)
                              </Typography>
                              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                                {result.ocrSimilarity.matchValueInvoice}%
                              </Typography>
                            </Paper>

                            <Paper sx={{ p: 2, minWidth: 200, bgcolor: 'background.paper' }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Vendor Match (bobot {result.ocrSimilarity.weights?.vendor ?? 20}%)
                              </Typography>
                              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                                {result.ocrSimilarity.matchValueVendor ?? 0}%
                              </Typography>
                              {(result.ocrSimilarity.extractedVendorName ||
                                result.ocrSimilarity.referenceVendorName) && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                  {result.ocrSimilarity.extractedVendorName || '—'}
                                  {' vs '}
                                  {result.ocrSimilarity.referenceVendorName || '—'}
                                </Typography>
                              )}
                            </Paper>

                            <Paper sx={{ p: 2, minWidth: 200, bgcolor: 'background.paper' }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Semantic OCR Match (bobot {result.ocrSimilarity.weights?.semanticText ?? 25}%)
                              </Typography>
                              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                                {result.ocrSimilarity.matchValueSemantic}%
                              </Typography>
                            </Paper>

                            <Paper sx={{ p: 2, minWidth: 200, bgcolor: 'background.paper' }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Category Match (bobot {result.ocrSimilarity.weights?.category ?? 10}%)
                              </Typography>
                              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                                {result.ocrSimilarity.matchValueCategory}%
                              </Typography>
                            </Paper>

                            <Paper sx={{ p: 2, minWidth: 200, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Invoice Date (bobot 0% — display only)
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Not included in similarity score. Shown with invoice number on SMART UI.
                              </Typography>
                            </Paper>
                          </Box>
                          
                          {result.ocrSimilarity.needsReview && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                This result requires manual review due to low similarity score.
                              </Typography>
                            </Alert>
                          )}
                          
                          {result.referenceRefId && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Compared against Reference Ref ID: {result.referenceRefId}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {!result.suspected && (
                        <Alert 
                          severity="success" 
                          sx={{ 
                            mb: 2,
                            '& .MuiAlert-icon': {
                              fontSize: '2rem'
                            }
                          }}
                        >
                          <Typography variant="h6" gutterBottom>
                            Content Check Passed
                          </Typography>
                          <Typography variant="body1">
                            No blacklisted content was found in the description.
                          </Typography>
                        </Alert>
                      )}
                      <Box sx={{ display: 'flex', gap: 4 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                          <Typography variant="body1" sx={{ 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word',
                            bgcolor: 'white',
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            minHeight: '200px'
                          }}>
                            {result.description}
                          </Typography>
                        </Box>
                        <Box sx={{ width: '300px' }}>
                          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="h6" gutterBottom>
                              Transaction Details
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Data Source
                              </Typography>
                              <Typography variant="body1" sx={{ color: result.dataSource === 'BigQuery' ? 'primary.main' : 'secondary.main' }}>
                                {result.dataSource}
                              </Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Amount
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                {result.amount}
                              </Typography>
                            </Box>
                            <Box sx={{ mb: 2, maxHeight: '100px', overflow: 'auto' }}>
                              <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                              <Typography variant="body1" sx={{ 
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}>
                                {result.categoryName}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">Invoice Number</Typography>
                              <Typography variant="body1">{result.invoiceNumber || '-'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">Parent ID</Typography>
                              <Typography variant="body1">{result.parentUniqueId || '-'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">Table</Typography>
                              <Typography variant="body1">{result.tableName}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">Transaction Date</Typography>
                              <Typography variant="body1">{result.transactionDate}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">Bank Account Number</Typography>
                              <Typography variant="body1">{result.bankAccountNumber || '-'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">Timestamp</Typography>
                              <Typography variant="body1">
                                {result.timestamp ? new Date(result.timestamp).toLocaleString() : '-'}
                              </Typography>
                            </Box>
                          </Paper>
                        </Box>
                      </Box>
                    </Paper>
                  )}
                  
                  {analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`] && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Analysis Results
                      </Typography>
                      <Paper sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">Date Match</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.datesMatch ? 'Matched' : 'Not Matched'} 
                                color={analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.datesMatch ? 'success' : 'error'}
                                size="small"
                              />
                            </Box>
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Original: {result.transactionDate || (
                                  <Box component="span" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                                    Not Supplied
                                  </Box>
                                )}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Matched: {analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.matchedDates || 'No date matched'}
                              </Typography>
                            </Box>
                            {analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.dates.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Extracted dates: {analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.dates.join(', ')}
                              </Typography>
                            )}
                          </Box>

                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">Invoice Match</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.invoiceMatch ? 'Matched' : 'Not Matched'} 
                                color={analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.invoiceMatch ? 'success' : 'error'}
                                size="small"
                              />
                            </Box>
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Original: {result.invoiceNumber || (
                                  <Box component="span" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                                    Not Supplied
                                  </Box>
                                )}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Matched: {analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.matchedInvoice || 'No invoice matched'}
                              </Typography>
                            </Box>
                          </Box>

                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">Amount Match</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {analysisLoading ? (
                                <Chip 
                                  label="Analyzing..." 
                                  color="default"
                                  size="small"
                                />
                              ) : (
                                <Chip 
                                  label={analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.amountMatch ? 'Matched' : 'Not Matched'} 
                                  color={analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.amountMatch ? 'success' : 'error'}
                                  size="small"
                                />
                              )}
                            </Box>
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Original: {result.amount || (
                                  <Box component="span" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                                    Not Supplied
                                  </Box>
                                )}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Matched: {analysisLoading ? 'Analyzing...' : (analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.matchedAmount || 'No amount matched')}
                              </Typography>
                              {/* Debug info */}
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                                Debug - amountMatch: {String(analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.amountMatch)}, matchedAmount: "{analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.matchedAmount}"
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                                Debug - Key: {`${result.refId}_${result.categoryType}`}, AnalysisLoading: {String(analysisLoading)}
                              </Typography>
                            </Box>
                            {!analysisLoading && analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.numbers.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Extracted numbers: {analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`]?.numbers.join(', ')}
                              </Typography>
                            )}
                          </Box>

                          {/* Bank Account Number Match - robust check */}
                          {(() => {
                            const analysis = analysisResults[`${result.refId}_${result.categoryType}_${result.tableName}`];
                            return (
                              <BankAccountNumberMatch
                                originalBankAccountNumber={result.bankAccountNumber}
                                analysis={analysis}
                              />
                            );
                          })()}
                        </Box>
                      </Paper>
                    </Box>
                  )}
            
                  <Typography variant="h6" gutterBottom>
                    Historical Semantic Similarity Matches
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Compares OCR text against previous documents using semantic similarity. High matches can appear
                    even when invoice numbers differ.
                  </Typography>
                  {result.similarityCheck.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No similar records found.
                    </Alert>
                  ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: '80vh' }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell align="right" width="8%">Similarity</TableCell>
                            <TableCell align="right" width="8%">Bobot</TableCell>
                            <TableCell width="8%">Ref ID</TableCell>
                            <TableCell width="8%">Amount</TableCell>
                            <TableCell width="8%">Category</TableCell>
                            <TableCell width="8%">Type</TableCell>
                            <TableCell width="14%">Invoice + Date</TableCell>
                            <TableCell width="8%">Parent ID</TableCell>
                            <TableCell width="6%">Table</TableCell>
                            <TableCell width="24%">Text</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.similarityCheck.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell align="right">
                                <Chip 
                                  label={`${(item.similarity * 100).toFixed(2)}%`}
                                  color={item.similarity * 100 >= 70 ? 'success' : item.similarity * 100 >= 50 ? 'warning' : 'error'}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="caption" display="block">
                                  {item.similarityBobot != null ? `${item.similarityBobot}%` : '—'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Amt {item.matchValueAmount ?? '—'} · Inv {item.matchValueInvoice ?? '—'} · Sem {item.matchValueSemantic ?? '—'} · Cat {item.matchValueCategory ?? '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>{item.refId}</TableCell>
                              <TableCell>{item.amount}</TableCell>
                              <TableCell>{item.categoryName}</TableCell>
                              <TableCell>{item.categoryType}</TableCell>
                              <TableCell>
                                {item.invoiceNumberAndDate ||
                                  [item.invoiceNumber, item.transactionDate].filter(Boolean).join(' | ') ||
                                  '—'}
                              </TableCell>
                              <TableCell>{item.parentUniqueId}</TableCell>
                              <TableCell>{item.tableName}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ 
                                  minWidth: 400,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  maxHeight: '300px',
                                  overflow: 'auto',
                                  bgcolor: 'grey.50',
                                  p: 2,
                                  borderRadius: 1,
                                  fontSize: '0.9rem',
                                  lineHeight: 1.5
                                }}>
                                  {item.text}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
      <ProcessProgressModal
        open={progressOpen}
        refId={refId}
        events={progressEvents}
        error={progressError}
        onClose={() => setProgressOpen(false)}
      />
      <Box sx={{ mt: 2, textAlign: 'center', color: 'grey.600', fontSize: 12, width: '100%', position: 'fixed', bottom: 8 }}>
        Build date: {buildDate}
      </Box>
    </Box>
  );
};

export default RefIdForm; 